import { Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';

import Swal from 'sweetalert2';
import { WorkflowForm } from 'app/models/WorkflowForm';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilityService } from 'app/services/utility.service';
import { LookupServices } from 'app/services/lookup.service';
import { NotificationService } from 'app/services/notification.service';
import { UserService } from 'app/services/user.service';
import { RumActionTrackerService } from 'app/services/rum-action-tracker.service';
import { SendBackReviewPayloadService } from 'app/services/send-back-review-payload.service';
import { SendBackReviewDialogService } from './send-back-review-dialog.service';

@Component({
	selector: 'client-workflow-approval',
	templateUrl: './workflow-approval.component.html',
	styleUrls: ['./workflow-approval.component.css'],
	standalone: false,
})
export class WorkflowApprovalComponent implements OnInit, OnChanges {
	hideApproval: boolean;
	currentWorkflow: any;
	hasAlreadyApproved: any;
	disableRejectionButton: any;
	rejectionReasonRequired: boolean;
	rejectionReasonValid: boolean;
	rejectionReason: any;
	private isRejected: boolean;
	isSearchForm: any;
	canSkipApprover = false;
	isOwner: boolean;
	isAdmin = false;
	isCurrentApprover = false;
	@Input() clientId: string;
	@Input() workflow: WorkflowForm;
	@Input() userId: string;
	@Input() canApprove: boolean;
	@Input() formId: string;
	@Output() readonly onApproveWorkflow: EventEmitter<any> = new EventEmitter<any>();
	@Output() readonly onRejectWorkflow: EventEmitter<any> = new EventEmitter<any>();
	@Input() hideDelete = false;
	@Input() hideDecline = false;
	@Input() hideTitle = false;
	@Input() canSendBack = false;
	@Input() disabledCanSendBackBtn = false;
	@Output() readonly sendBack: EventEmitter<any> = new EventEmitter<any>();
	@Input() formType: string;
	isButtonDisabled = false;
	isViewOnly = false;
	constructor(
		private activatedRoute: ActivatedRoute,
		private utilityService: UtilityService,
		private userService: UserService,
		private lookupService: LookupServices,
		private notificationService: NotificationService,
		private router: Router,
		private rumActionTracker: RumActionTrackerService,
		private sendBackPayloadService: SendBackReviewPayloadService,
		private sendBackReviewDialogService: SendBackReviewDialogService
	) {
		this.isSearchForm = this.activatedRoute.snapshot.queryParamMap.get('form-search');
		this.isAdmin = this.userService.isAdmin();
		this.isViewOnly = this.userService.isReadOnlyView();
	}

	ngOnInit(): void {
		this.checkPermission();
	}

	checkPermission() {
		let user = this.userService.currentUserValue.user;
		if (this.workflow?.workflowName && !this.formType) {
			this.formType = this.userService.getFormKeyByName(this.workflow.workflowName);
		}
		if (user.claims && user.claims[this.clientId] && user.claims[this.clientId]) {
			this.canSkipApprover =
				user.claims[this.clientId]?.claims?.find((x) => x.id == this.formType)?.skipApprover ?? false;
		}
		this.isOwner = this.isFormOwner();
		this.isCurrentApprover = this.isApprover();
		this.hasAlreadyApproved = this.workflow != null && this.utilityService.hasApproved(this.workflow, this.userId);
	}

	// DO NOT REMOVE THIS METHOD
	ngOnChanges() {
		this.isRejected = this.workflow?.declinedReason != null && this.workflow?.declinedReason?.length > 0;
		this.hideApproval = this.isRejected;

		if (this.workflow?.workflowSteps) {
			for (let step of this.workflow?.workflowSteps) {
				if (step.isCurrent) {
					this.currentWorkflow = step;
				}
			}
		}
		this.checkPermission();
	}

	approveWorkflow(isSkipApprover: boolean) {
		let defaultText = 'Are you sure you want to approve this form?';

		if (isSkipApprover == false) {
			this.isButtonDisabled = true;
		}
		if (this.workflow?.currentApprovers?.length > 0) {
			if (this.workflow?.currentApprovers.find((x) => x.userId == this.userId) === undefined) {
				defaultText =
					'Are you sure you want to approve this form on the behalf of ' + this.workflow?.currentApprovers[0].name;
			}
		}

		if (isSkipApprover) {
			defaultText = `Are you sure you want to Skip Approval on behalf of ${this.workflow?.currentApprovers[0].name}?`;
		}

		Swal.fire({
			title: 'Are you sure?',
			text: defaultText,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'I am sure!',
			cancelButtonText: 'No, cancel it!',
			customClass: {
				confirmButton: 'btn btn-success', // Replaces confirmButtonClass
				cancelButton: 'btn btn-danger', // Replaces cancelButtonClass
			},
			buttonsStyling: false,
		}).then((result) => {
			if (result.value) {
				this.rumActionTracker.trackAction(isSkipApprover ? 'Skip' : 'Approve', {
					formType: this.formType || this.workflow?.workflowName,
				});
				this.rejectionReasonRequired = false;
				this.onApproveWorkflow.emit(() => {
					Swal.fire({
						title: 'Approved!',
						text: 'This form has been approved.',
						icon: 'success',
						customClass: {
							confirmButton: 'btn btn-success', // Replaces confirmButtonClass
						},
						buttonsStyling: false,
					}).then();

					if (this.isSearchForm == '1') {
						window.opener = self;
						window.close();
					}

					this.utilityService.scrollToTop();
				});
			} else {
				Swal.fire({
					title: 'Cancelled',
					text: 'This form has not been approved.',
					icon: 'error',
					customClass: {
						confirmButton: 'btn btn-info', // Replaces confirmButtonClass
					},
					buttonsStyling: false,
				}).then();
			}
			this.isButtonDisabled = false;
		});
	}

	rejectWorkflow() {
		this.rejectionReasonRequired = true;
		if (this.rejectionReasonValid && this.rejectionReason.length > 0) {
			Swal.fire({
				title: 'Are you sure?',
				text: 'Are you sure you want to decline this form?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'I am sure!',
				cancelButtonText: 'No, cancel it!',
				customClass: {
					confirmButton: 'btn btn-success', // Replaces confirmButtonClass
					cancelButton: 'btn btn-danger', // Replaces cancelButtonClass
				},
				buttonsStyling: false,
			}).then((result) => {
				if (result.value) {
					this.rumActionTracker.trackAction('Reject', { formType: this.formType || this.workflow?.workflowName });
					const val = {
						reason: this.rejectionReason,
						workflowId: this.currentWorkflow?.id,
						callback: () => {
							Swal.fire({
								title: 'Declined!',
								text: 'This form has been declined.',
								icon: 'success',
								customClass: {
									confirmButton: 'btn btn-success', // Replaces confirmButtonClass
								},
								buttonsStyling: false,
							}).then();

							if (this.isSearchForm == '1') {
								window.opener = self;
								window.close();
							}
						},
					};
					this.onRejectWorkflow.emit(val);
				} else {
					Swal.fire({
						title: 'Cancelled',
						text: 'This form was not declined.',
						icon: 'error',
						customClass: {
							confirmButton: 'btn btn-info', // Replaces confirmButtonClass
						},
						buttonsStyling: false,
					}).then();
				}
			});
		} else {
			this.disableRejectionButton = true;
		}
	}

	rejectionReasonChanged() {
		this.rejectionReasonValid = this.rejectionReason && this.rejectionReason.length > 0;
		if (this.rejectionReasonValid) {
			this.disableRejectionButton = false;
		}
	}

	isApprover() {
		if (this.workflow != null) {
			return this.utilityService.isApprover(this.workflow, this.userId);
		}
		return false;
	}

	deleteForm() {
		if (
			!this.isDeletableFormType(this.formType) &&
			this.workflow?.workflowStep === 'Completed' &&
			this.workflow?.isCompleted
		) {
			Swal.fire({
				title: 'Action Not Allowed',
				text: 'You cannot delete a completed payment form. Please use the "Void & Reissue" form to void the payment instead.',
				icon: 'info',
				customClass: {
					confirmButton: 'btn btn-primary',
				},
				buttonsStyling: false,
			});
			return;
		}
		Swal.fire({
			title: 'Are you sure?',
			text: 'Are you sure you want to delete this form?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'I am sure!',
			cancelButtonText: 'No, cancel it!',
			customClass: {
				confirmButton: 'btn btn-success', // Replaces confirmButtonClass
				cancelButton: 'btn btn-danger', // Replaces cancelButtonClass
			},
			buttonsStyling: false,
		}).then((result) => {
			if (result.value) {
				this.lookupService.deleteForm(this.clientId, this.formId, this.formType).subscribe(
					() => {
						this.notificationService.showSuccess('Form has been disabled', 'Success');
						this.router.navigate(['dashboard']).then();
					},
					(error) => {
						this.notificationService.showError(error);
					}
				);
			} else {
				Swal.fire({
					title: 'Cancelled',
					text: 'This form has not been deleted.',
					icon: 'error',
					customClass: {
						confirmButton: 'btn btn-info', // Replaces confirmButtonClass
					},
					buttonsStyling: false,
				}).then();
			}
		});
	}

	sendBackToAddressComments() {
		this.sendBackReviewDialogService.open().then((result) => {
			if (result) {
				this.rumActionTracker.trackAction('Send Back', { formType: this.formType || this.workflow?.workflowName });
				this.sendBackPayloadService.set(this.formId, {
					comment: result.comment,
					attachments: result.attachments,
				});
				this.sendBack.emit();
			} else {
				Swal.fire({
					title: 'Cancelled',
					text: 'Send back to address action cancelled.',
					icon: 'error',
					customClass: {
						confirmButton: 'btn btn-info', // Replaces confirmButtonClass
					},
					buttonsStyling: false,
				}).then();
			}
		});
	}

	isFormOwner() {
		if (this.workflow != null) {
			return this.workflow.createdBy == this.userService.currentUserValue.user.id;
		}
		return false;
	}
	private isDeletableFormType(formType: string): boolean {
		const nonDeletableTypes = this.utilityService.getPaymentFormTypes();
		return !nonDeletableTypes.includes(formType.toLowerCase());
	}

	canShowDeleteButton(): boolean {
		const nonDeletableTypes = [...this.utilityService.getPaymentFormTypes()];
		const billingARForms = ['customer-invoice-form', 'quick-invoice-form', 'invoice-adjustment-form'];
		const cashieringForms = ['cash-receipt-form', 'cash-bond-form', 'cashiering-report', 'third-party-integration-form'];
		const generalLedgerForms = ['journal-entry'];
		nonDeletableTypes.push(...billingARForms, ...cashieringForms, ...generalLedgerForms);
		if (
			nonDeletableTypes.includes(this.formType?.toLowerCase()) &&
			(this.workflow?.workflowStep === 'Completed' || this.workflow?.workflowStep === 'Denied')
		) {
			return false;
		}

		return (
			(this.isAdmin && !this.isViewOnly) ||
			((!this.workflow?.workflowId ||
				this.workflow?.workflowStep === 'Workflow Started' ||
				this.workflow?.workflowStep === 'DRAFT') &&
				this.isOwner &&
				this.formType !== '' &&
				this.formId !== '')
		);
	}
}
